<template>
  <modal
    :title="dialogTitle"
    @close_modal="close"
    :loading="saving"
  >
    <template v-slot:content>
      <v-text-field
        v-model="name"
        :label="$t('reports.field_name')"
        required
        class="mb-4"
        data-testid="report-name"
      />
      <v-btn-toggle
        v-model="reportType"
        mandatory
        density="compact"
        color="primary"
        divided
        class="mb-4"
        data-testid="report-type-toggle"
      >
        <v-btn value="custom" size="small" data-testid="report-type-custom">
          {{ $t('reports.type_custom') }}
        </v-btn>
        <v-btn value="monthly" size="small" data-testid="report-type-monthly">
          {{ $t('reports.type_monthly') }}
        </v-btn>
      </v-btn-toggle>
      <v-row v-if="reportType === 'monthly'" dense class="mb-4">
        <v-col cols="12" md="6">
          <v-text-field
            v-model="startDate"
            type="date"
            :label="$t('reports.field_start_date')"
            density="compact"
            hide-details="auto"
            required
            data-testid="report-start-date"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="endDate"
            type="date"
            :label="$t('reports.field_end_date')"
            :hint="$t('reports.end_date_hint')"
            persistent-hint
            density="compact"
            data-testid="report-end-date"
          />
        </v-col>
      </v-row>
      <p class="text-caption text-medium-emphasis mb-2">
        {{ $t('reports.drag_hint') }}
      </p>
      <div
        v-for="(item, index) in items"
        :key="item._key"
        class="report-item-card mb-2"
        :class="{
          'report-item-card--dragging': dragFrom === index,
          'report-item-card--expanded': expandedKey === item._key,
        }"
        :data-testid="'report-item-' + index"
        @dragover.prevent="onDragOver(index)"
        @drop.prevent="onDrop(index)"
      >
        <div
          class="report-item-summary"
          :data-testid="'report-item-summary-' + index"
          @click="toggleExpanded(item._key)"
        >
          <span
            class="report-drag-handle"
            draggable="true"
            data-testid="report-item-drag-handle"
            :title="$t('reports.drag_hint')"
            @click.stop
            @dragstart="onDragStart(index, $event)"
            @dragend="onDragEnd"
          >
            <v-icon size="small">fas fa-grip-vertical</v-icon>
          </span>
          <v-icon size="x-small" class="report-item-chevron">
            {{ expandedKey === item._key ? 'fas fa-chevron-down' : 'fas fa-chevron-right' }}
          </v-icon>
          <span class="report-item-name text-truncate">
            {{ itemDisplayName(item) }}
          </span>
          <span class="report-item-kind text-caption text-medium-emphasis">
            {{ item.kind === 'formula' ? $t('reports.kind_formula') : $t('reports.kind_filter') }}
          </span>
          <v-btn
            icon
            variant="text"
            color="error"
            size="small"
            class="report-item-delete"
            @click.stop="removeItem(index)"
            :disabled="items.length <= 1"
          >
            <v-icon size="small">fas fa-trash</v-icon>
          </v-btn>
        </div>
        <div v-if="expandedKey === item._key" class="report-item-body pa-3 pt-1">
          <v-row dense align="center">
            <v-col cols="12" md="4">
              <v-text-field
                v-model="item.id"
                :label="$t('reports.field_variable_id')"
                density="compact"
                hide-details
              />
            </v-col>
            <v-col cols="12" md="8">
              <v-text-field
                v-model="item.label"
                :label="$t('reports.field_label')"
                density="compact"
                hide-details
              />
            </v-col>
          </v-row>
          <v-checkbox
            v-model="item.show"
            :label="$t('reports.show_in_graph_table')"
            density="compact"
            hide-details
            class="mt-2 mb-2"
            :data-testid="'report-item-show-' + index"
          />
          <v-btn-toggle
            v-model="item.kind"
            mandatory
            density="compact"
            color="primary"
            divided
            class="mb-2"
          >
            <v-btn value="filter" size="small">{{ $t('reports.kind_filter') }}</v-btn>
            <v-btn value="formula" size="small">{{ $t('reports.kind_formula') }}</v-btn>
          </v-btn-toggle>
          <div v-if="item.kind === 'filter'">
            <filter-tree
              :ref="'filterTree' + index"
              :max_results="false"
              :show_button="false"
              :show_limit="false"
              :loaded-filters="item.filters"
              :title="$t('reports.filter_title')"
              @update="(filters) => updateItemFilters(index, filters)"
            />
          </div>
          <v-text-field
            v-else
            v-model="item.formula"
            :label="$t('reports.field_formula')"
            :hint="$t('reports.formula_hint')"
            persistent-hint
            density="compact"
            data-testid="report-formula"
          />
        </div>
      </div>
      <v-row>
        <v-col cols="auto">
          <v-btn variant="outlined" size="small" @click="addFilterItem">
            {{ $t('reports.btn_add_filter') }}
          </v-btn>
        </v-col>
        <v-col cols="auto">
          <v-btn variant="outlined" size="small" @click="addFormulaItem">
            {{ $t('reports.btn_add_formula') }}
          </v-btn>
        </v-col>
      </v-row>
    </template>
    <template v-slot:buttons>
      <v-btn color="primary" variant="text" @click="close">
        {{ $t('reports.btn_cancel') }}
      </v-btn>
      <v-btn color="primary" variant="flat" @click="save" data-testid="report-save">
        {{ $t('reports.btn_save') }}
      </v-btn>
    </template>
  </modal>
</template>

<script>
import Modal from '@/components/Modal';
import FilterTree from '@/components/asset_filter/FilterTree';

let nextKey = 1;

function emptyFilterItem(id) {
  return {
    _key: nextKey++,
    id,
    label: '',
    kind: 'filter',
    filters: [],
    formula: '',
    show: true,
  };
}

function emptyFormulaItem(id) {
  return {
    _key: nextKey++,
    id,
    label: '',
    kind: 'formula',
    filters: [],
    formula: '',
    show: true,
  };
}

function itemIsShown(item) {
  return item == null || item.show !== false;
}

function defaultStartDate() {
  const d = new Date();
  return `${d.getFullYear()}-01-01`;
}

export default {
  name: 'EditReportDialog',
  components: { Modal, FilterTree },
  props: {
    report: { default: null },
  },
  emits: ['close', 'saved'],
  data() {
    const first = emptyFilterItem('id1');
    return {
      name: '',
      reportType: 'custom',
      startDate: defaultStartDate(),
      endDate: '',
      items: [first],
      expandedKey: first._key,
      saving: false,
      dragFrom: null,
    };
  },
  computed: {
    dialogTitle() {
      return this.report
        ? this.$t('reports.edit_title')
        : this.$t('reports.create_title');
    },
  },
  mounted() {
    if (this.report) {
      this.name = this.report.name || '';
      const def = this.report.definition || {};
      this.reportType = def.type === 'monthly' ? 'monthly' : 'custom';
      this.startDate = def.start_date || defaultStartDate();
      this.endDate = def.end_date || '';
      const rawItems = Array.isArray(def.items) ? def.items : [];
      this.items = rawItems.map((item) => {
        const kind = item.formula ? 'formula' : 'filter';
        return {
          _key: nextKey++,
          id: item.id || '',
          label: item.label || '',
          kind,
          filters: item.filters ? JSON.parse(JSON.stringify(item.filters)) : [],
          formula: item.formula || '',
          show: itemIsShown(item),
        };
      });
      if (this.items.length === 0) {
        this.items = [emptyFilterItem('id1')];
      }
      // Existing reports start collapsed so many variables stay scannable.
      this.expandedKey = null;
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    itemDisplayName(item) {
      const label = (item.label || '').trim();
      if (label) {
        return label;
      }
      const id = (item.id || '').trim();
      if (id) {
        return id;
      }
      return item.kind === 'formula'
        ? this.$t('reports.kind_formula')
        : this.$t('reports.kind_filter');
    },
    toggleExpanded(key) {
      this.expandedKey = this.expandedKey === key ? null : key;
    },
    onDragStart(index, event) {
      this.dragFrom = index;
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(index));
      }
    },
    onDragOver(index) {
      if (this.dragFrom === null || this.dragFrom === index) {
        return;
      }
      const from = this.dragFrom;
      const moved = this.items.splice(from, 1)[0];
      this.items.splice(index, 0, moved);
      this.dragFrom = index;
    },
    onDrop(index) {
      this.onDragOver(index);
      this.dragFrom = null;
    },
    onDragEnd() {
      this.dragFrom = null;
    },
    updateItemFilters(index, filters) {
      const item = this.items[index];
      if (!item) {
        return;
      }
      const next = filters == null ? [] : filters;
      if (JSON.stringify(next) === JSON.stringify(item.filters || [])) {
        return;
      }
      item.filters = JSON.parse(JSON.stringify(next));
    },
    removeItem(index) {
      const removed = this.items[index];
      this.items.splice(index, 1);
      if (removed && this.expandedKey === removed._key) {
        this.expandedKey = this.items.length ? this.items[0]._key : null;
      }
    },
    addFilterItem() {
      const n = this.items.filter((i) => i.kind === 'filter').length + 1;
      const item = emptyFilterItem(`id${n}`);
      this.items.push(item);
      this.expandedKey = item._key;
    },
    addFormulaItem() {
      const n = this.items.length + 1;
      const item = emptyFormulaItem(`id${n}`);
      this.items.push(item);
      this.expandedKey = item._key;
    },
    buildDefinition() {
      const definition = {
        items: this.items.map((item) => {
          const row = {
            id: item.id.trim(),
            label: item.label.trim(),
            show: item.show !== false,
          };
          if (item.kind === 'formula') {
            row.formula = item.formula.trim();
          } else {
            row.filters = item.filters || [];
          }
          return row;
        }),
      };
      if (this.reportType === 'monthly') {
        definition.type = 'monthly';
        definition.start_date = this.startDate;
        if (this.endDate) {
          definition.end_date = this.endDate;
        }
      } else {
        definition.type = 'custom';
      }
      return definition;
    },
    async save() {
      if (!this.name.trim()) {
        return;
      }
      if (this.reportType === 'monthly' && !this.startDate) {
        return;
      }
      this.saving = true;
      try {
        const definition = this.buildDefinition();
        let res;
        if (this.report && this.report.id) {
          res = await this.$api.report.update(this.report.id, this.name.trim(), definition);
        } else {
          res = await this.$api.report.create(this.name.trim(), definition);
        }
        this.$emit('saved', (res && res.report) || res);
        this.close();
      } catch (e) {
        console.error('Save report failed', e);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.report-item-card {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  background: #fff;
  overflow: hidden;
}
.report-item-card--dragging {
  opacity: 0.55;
}
.report-item-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  cursor: pointer;
  user-select: none;
}
.report-item-summary:hover {
  background: rgba(0, 0, 0, 0.03);
}
.report-item-chevron {
  color: rgba(0, 0, 0, 0.45);
  flex: 0 0 auto;
}
.report-item-name {
  flex: 1 1 auto;
  min-width: 0;
  font-weight: 500;
}
.report-item-kind {
  flex: 0 0 auto;
  margin-right: 4px;
}
.report-item-delete {
  flex: 0 0 auto;
}
.report-item-body {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.report-drag-handle {
  cursor: grab;
  color: rgba(0, 0, 0, 0.45);
  display: inline-flex;
  align-items: center;
  padding: 4px;
  user-select: none;
  flex: 0 0 auto;
}
</style>
